import { Component } from '@theme/component';

/**
 * Bundle Contents Component
 * Fetches bundle component products via Storefront API and displays them grouped by category
 */
class BundleContents extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();
    if (!this.#loaded) {
      this.#loadBundleContents();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
  }

  /**
   * Load the bundle contents
   */
  async #loadBundleContents() {
    const { productId, variantId, storefrontToken, shopDomain } = this.dataset;

    // Bail out for placeholder/onboarding products. Liquid renders `product.id`
    // as an empty string for placeholders, so treat any non-numeric / non-GID
    // value as "no real product" and hide the element without fetching.
    if (!productId || !/^(\d+|gid:\/\/shopify\/Product\/\d+)$/.test(productId.trim())) {
      this.classList.add('hidden');
      return;
    }

    if (!storefrontToken) {
      console.error('Bundle contents: Missing storefront token');
      return;
    }

    try {
      // First, check if this product is a bundle and get components
      const bundleData = await this.#fetchBundleComponents(
        productId,
        variantId,
        storefrontToken,
        shopDomain
      );

      if (!bundleData || !bundleData.isBundle || bundleData.components.length === 0) {
        // Not a bundle or no components - hide the element
        this.classList.add('hidden');
        return;
      }

      // Get category mappings from data attribute (passed from Liquid)
      const categoryMappings = this.#getCategoryMappings();

      if (!categoryMappings || categoryMappings.length === 0) {
        console.warn('Bundle contents: No category mappings found');
        this.classList.add('hidden');
        return;
      }

      // Group components by product type and count
      const typeCounts = this.#countProductTypes(bundleData.components);

      // Render the contents
      this.#render(typeCounts, categoryMappings);
      this.#loaded = true;
    } catch (error) {
      console.error('Bundle contents error:', error);
      this.classList.add('hidden');
    }
  }

  /**
   * Fetch bundle components from Storefront API
   * @param {string} productId
   * @param {string | undefined} variantId
   * @param {string} storefrontToken
   * @param {string} shopDomain
   */
  async #fetchBundleComponents(productId, variantId, storefrontToken, shopDomain) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const productGid = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

    // Query to check if bundle and get components
    const query = `
      query GetBundleComponents($productId: ID!) {
        product(id: $productId) {
          id
          title
          variants(first: 10) {
            edges {
              node {
                id
                title

                # TRUE when this variant is only purchasable as a bundle parent
                requiresComponents

                # Bundle components (Storefront API)
                components(first: 50) {
                  nodes {
                    productVariant {
                      id
                      title
                      product {
                        id
                        title
                        productType
                      }
                    }
                    quantity
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${shopDomain}/api/2026-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({
        query,
        variables: { productId: productGid },
      }),
      signal: this.#activeFetch.signal,
    });

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status}`);
    }

    const data = await response.json();
    const product = data?.data?.product;

    if (!product) {
      return { isBundle: false, components: [] };
    }

    // Find the variant that's a bundle (or use first variant with components)
    let bundleVariant = null;

    if (variantId) {
      const variantGid = variantId.includes('gid://') ? variantId : `gid://shopify/ProductVariant/${variantId}`;
      bundleVariant = product.variants.edges.find((e) => e.node.id === variantGid)?.node;
    }

    // If no specific variant or it doesn't require components, find first bundle variant
    if (!bundleVariant?.requiresComponents) {
      bundleVariant = product.variants.edges.find((e) => e.node.requiresComponents)?.node;
    }

    if (!bundleVariant?.requiresComponents) {
      return { isBundle: false, components: [] };
    }

    const components = bundleVariant.components.nodes.map((node) => ({
      productType: node.productVariant.product.productType,
      productTitle: node.productVariant.product.title,
      variantTitle: node.productVariant.title,
      quantity: node.quantity,
    }));

    return { isBundle: true, components };
  }

  /**
   * Get category mappings from data attribute
   * @returns {Array<{categoryName: string, icon: string, productTypes: string[]}>}
   */
  #getCategoryMappings() {
    try {
      const mappingsJson = this.dataset.categoryMappings;
      if (!mappingsJson) return [];
      return JSON.parse(mappingsJson);
    } catch {
      console.error('Bundle contents: Invalid category mappings JSON');
      return [];
    }
  }

  /**
   * Count products by type
   * @param {Array<{productType: string, quantity: number}>} components
   * @returns {Map<string, number>}
   */
  #countProductTypes(components) {
    const counts = new Map();

    for (const component of components) {
      const type = (component.productType || '').toLowerCase().trim();
      if (!type) continue;

      const currentCount = counts.get(type) || 0;
      // Count unique products (option 1) - each component counts as 1
      counts.set(type, currentCount + 1);
    }

    return counts;
  }

  /**
   * Render the bundle contents
   * @param {Map<string, number>} typeCounts
   * @param {Array<{categoryName: string, icon: string, productTypes: string[]}>} categoryMappings
   */
  #render(typeCounts, categoryMappings) {
    const listElement = this.querySelector('.bundle-contents__list');
    if (!listElement) return;

    // Clear existing content
    listElement.innerHTML = '';

    for (const mapping of categoryMappings) {
      const matchedTypes = [];
      let categoryTotal = 0;

      for (const mappedType of mapping.productTypes) {
        const normalizedType = mappedType.toLowerCase().trim();
        const count = typeCounts.get(normalizedType);

        if (count && count > 0) {
          matchedTypes.push(mappedType);
          categoryTotal += count;
        }
      }

      if (categoryTotal > 0) {
        const item = document.createElement('li');
        item.className = 'bundle-contents__item';

        let iconHtml = '';
        if (mapping.icon) {
          iconHtml = `<img src="${mapping.icon}" alt="" class="bundle-contents__icon" loading="lazy">`;
        }

        item.innerHTML = `
          ${iconHtml}
          <span class="bundle-contents__type">
            ${mapping.categoryName}:
            <span class="bundle-contents__details">
              ${matchedTypes.join(', ')} (${categoryTotal} pcs)
            </span>
          </span>
        `;

        listElement.appendChild(item);
      }
    }

    // If no categories matched, hide the component
    if (listElement.children.length === 0) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');
    }
  }
}

if (!customElements.get('bundle-contents')) {
  customElements.define('bundle-contents', BundleContents);
}
